import { zodResolver } from '@hookform/resolvers/zod'
import { AvailableLanguages } from '@repo/toolkit/language-types'
import { TFunction } from 'i18next'
import { useForm } from 'react-hook-form'
import { z, ZodType } from 'zod'

import useTranslate from '../../../lib/hooks/use-translate'
import {
  OtoIndividualTransferPerson,
  OtoIndividualTransferPersonInput
} from '../../../lib/types/models/enrollment/oto-individual-enrollments'
import { BaseFormProps } from '../../../lib/types/utils'
import { getSubmitAction } from '../../../lib/utils/buttons-config/validation-buttons-config'
import { PHONE_NUMBER_SCHEMA } from '../../../lib/utils/validations/phone-number.validation'
import { createStringSchema } from '../../../lib/utils/validations/string-validation'
import { FORM_MODE } from '../../../utils/constants/form.constants'
import Form, { FormControl, FormField, FormItem } from '../../elements/forms/form'
import ControlledInput from '../../elements/inputs/controlled-input'
import ControlledPhoneInput from '../../elements/inputs/controlled-phone-input'
import ValidationButtons from '../../elements/validation-buttons'

type Props = BaseFormProps<OtoIndividualTransferPersonInput, true> & {
  className?: string
}

const formSchema: (
  t: TFunction<AvailableLanguages, undefined>
) => ZodType<OtoIndividualTransferPersonInput> = (t) =>
  z.object({
    lastName: createStringSchema(t('oto.individual.placements.transfer.person.last.name')),
    firstName: createStringSchema(t('oto.individual.placements.transfer.person.first.name')),
    phoneNumber: PHONE_NUMBER_SCHEMA(t),
    email: createStringSchema(t('oto.individual.placements.transfer.person.email'), {
      isEmail: true
    })
  })

// the recorded person prefills the form -- its id is not part of what the provider submits
export const toOtoIndividualTransferPersonInput = (
  transferPerson?: OtoIndividualTransferPerson
): Partial<OtoIndividualTransferPersonInput> | undefined =>
  transferPerson
    ? {
        firstName: transferPerson.firstName,
        lastName: transferPerson.lastName,
        phoneNumber: transferPerson.phoneNumber,
        email: transferPerson.email
      }
    : undefined

// The provider records who it sends to the airport for a stay with accueil transfert.
const OtoIndividualTransferPersonForm = ({
  defaultValues,
  className,
  ...validationButtonProps
}: Props) => {
  const { t } = useTranslate()
  const form = useForm<OtoIndividualTransferPersonInput>({
    resolver: zodResolver(formSchema(t)),
    defaultValues,
    mode: FORM_MODE
  })

  return (
    <Form form={form}>
      <form onSubmit={form.handleSubmit(getSubmitAction(validationButtonProps.buttonsConfig))}>
        <div className="divide-y divide-white-active">
          <div className={className}>
            <div className="grid mobile:grid-cols-1 grid-cols-2 gap-4">
              <FormField
                control={form.control}
                name={'lastName'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        required
                        type={'text'}
                        label={t('oto.individual.placements.transfer.person.last.name')}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'firstName'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        required
                        type={'text'}
                        label={t('oto.individual.placements.transfer.person.first.name')}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'phoneNumber'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledPhoneInput
                        {...field}
                        required
                        defaultCountry={'FR'}
                        label={t('oto.individual.placements.transfer.person.phone.number')}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name={'email'}
                render={({ field }) => (
                  <FormItem>
                    <FormControl>
                      <ControlledInput
                        {...field}
                        required
                        type={'email'}
                        label={t('oto.individual.placements.transfer.person.email')}
                      />
                    </FormControl>
                  </FormItem>
                )}
              />
            </div>
          </div>
          <div className="pb-6 px-4">
            <ValidationButtons {...validationButtonProps} />
          </div>
        </div>
      </form>
    </Form>
  )
}

export default OtoIndividualTransferPersonForm
